<template>
  <ToastModal
    ref="modal"
    :title="titleToShow"
    show-mobile-footer
    modal-id="SignupOutOfStockModal"
    :prevent-close="submitted"
    :show-close-button="!submitted"
  >
    <div v-loading="loading">
      <div v-if="submitted" class="py-7">
        <div class="flex justify-center py-7">
          <OSvg svg="checkmark-icon" />
        </div>
        <p
          class="o-body text-neutral-800 mb-6 text-center"
          v-text="successMessage"
        />
      </div>
      <div v-else>
        <p
          class="o-body text-neutral-800 mb-6"
          v-text="descriptionToShow"
        />
        <div class="mt-5">
          <CreateAccountForm
            ref="accountForm"
            :first-name="form.firstName"
            :last-name="form.lastName"
            :errors="formatErrors(errors)"
            :email="form.email"
            :custom-ids="customIds"
            mock-password
            :country="country"
            @change="onChange"
            @submit="onSubmit"
          />
        </div>
      </div>
    </div>
    <template v-slot:footer>
      <ToastModalFooter
        v-bind="toastModalFooterProps"
        @click="onSubmit"
      />
    </template>
  </ToastModal>
</template>

<script>
import ToastModal from '@/components/ToastModal'
import ToastModalFooter from '@/components/ToastModal/ToastModalFooter'
import CreateAccountForm from '@/components/Forms/CreateAccountForm'
import { CountryCodes } from '@/enums/general'
import { SIGNUP_ADD_WAITING_LIST } from '@/utils/store/actionTypes'
import { mapActions } from 'vuex'
import ErrorsMixin from '@/mixins/errors'
import OSvg from '@/components/OSvg'
import { WebsiteCategoryCookie } from '@/enums/app'
import { getCookieDomain } from '@/utils/cookie'
import { togglePageScroll } from '@/utils/general'

export default {
  name: 'SignupOutOfStockModal',
  components: { ToastModal, ToastModalFooter, CreateAccountForm, OSvg },
  mixins: [ErrorsMixin],
  props: {
    title: {
      type: String,
      default: ''
    },
    secondaryTitle: {
      type: String,
      default: ''
    },
    description: {
      type: String,
      default: ''
    },
    secondaryDescription: {
      type: String,
      default: ''
    },
    successMessage: {
      type: String,
      default: ''
    },
    cta: {
      type: String,
      default: ''
    },
    submittedCta: {
      type: String,
      default: ''
    },
    country: {
      type: String,
      default: CountryCodes.netherlands
    },
    design: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      form: {
        firstName: '',
        lastName: '',
        email: '',
        productGroupSize: ''
      },
      errors: {},
      loading: false,
      showSecondaryTexts: false,
      submitted: false
    }
  },
  computed: {
    titleToShow () {
      if (this.submitted) {
        return ''
      }

      return this.showSecondaryTexts ? this.secondaryTitle : this.title
    },
    descriptionToShow () {
      return this.showSecondaryTexts ? this.secondaryDescription : this.description
    },
    toastModalFooterProps () {
      return {
        variant: this.submitted ? 'outline' : 'solid',
        color: this.submitted ? 'black' : 'dark-green',
        text: this.submitted ? this.submittedCta : this.cta,
        disabled: this.loading
      }
    },
    customIds () {
      return {
        firstName: 'firstName-out-of-stock',
        lastName: 'lastName-out-of-stock',
        email: 'email-out-of-stock',
        password: 'password-out-of-stock'
      }
    }
  },
  methods: {
    async onSubmit () {
      if (this.submitted) {
        this.$cookies.remove(WebsiteCategoryCookie, {
          path: '/',
          domain: getCookieDomain()
        })
        togglePageScroll(false)
        this.$router.push('/')
        return
      }

      this.loading = true

      this.$refs.accountForm.$v.$touch()

      if (this.$refs.accountForm.$v.$invalid) {
        this.$refs.accountForm.invalidForm = true
        return
      }

      const payload = {
        firstName: this.form.firstName,
        lastName: this.form.lastName,
        email: this.form.email,
        design: this.design,
        productGroupSize: this.productGroupSize
      }

      try {
        await this.addWaitingList(payload)
        this.submitted = true
      } catch (error) {
        const errors = this.getPopulatedErrors(error)

        if (errors) {
          this.errors = errors
        } else {
          this.$otoaster.danger(this.$t('Error.general'))
          this.$ota.general.logError(error)
        }
      } finally {
        this.loading = false
      }
    },
    onChange ({ value, field }) {
      this.form[field] = value
    },
    toggleActivation (productGroupSize = '', showSecondaryTexts = false, email = '', firstName = '', lastName = '') {
      this.submitted = false
      this.productGroupSize = productGroupSize
      this.showSecondaryTexts = showSecondaryTexts
      if (email) {
        this.form.email = email
      }
      if (firstName) {
        this.form.firstName = firstName
      }
      if (lastName) {
        this.form.lastName = lastName
      }
      this.$refs.modal.toggleActivation()
    },
    onModalClose () {
      this.$refs.modal.onModalClose()
    },
    formatErrors (errors) {
      return Object.values(errors).reduce((acc, error) => ({
        ...acc,
        [error.propertyPath]: error.populatedError
      }), {})
    },
    ...mapActions('signup', {
      addWaitingList: SIGNUP_ADD_WAITING_LIST
    })
  }
}
</script>

<style lang="postcss" scoped>
.create-account-form {
  >>> {
    .password-input {
      @apply hidden;
    }

    .create-account-form-bottom {
      @apply pt-3;
    }
  }
}
</style>
